const MAX_TRACKED_LIVE_TALK_TURNS = 256;

type RegisteredTurn = {
  order: number;
  executionId?: string;
  /** Sent after the turn it followed had ended, so it talks over nothing. */
  queuesBehindReply: boolean;
};

export type LiveTalkOwnerDecision = "adopted" | "duplicate" | "stale";

let nextOrder = 0;
let latestOwnerOrder = 0;
type LiveTalkOwner = {
  requestId: string;
  executionId: string;
  order: number;
};

let currentOwner: LiveTalkOwner | null = null;
/** The owner a user turn last silenced, until a newer turn owns speech. */
let retiredOwner: LiveTalkOwner | null = null;
const registeredTurns = new Map<string, RegisteredTurn>();
const retiredExecutionIds = new Set<string>();

function trimBoundedMap<K, V>(map: Map<K, V>): void {
  while (map.size > MAX_TRACKED_LIVE_TALK_TURNS) {
    const oldest = map.keys().next().value;
    if (oldest === undefined) break;
    map.delete(oldest);
  }
}

function rememberRetired(executionId: string): void {
  retiredExecutionIds.add(executionId);
  while (retiredExecutionIds.size > MAX_TRACKED_LIVE_TALK_TURNS) {
    const oldest = retiredExecutionIds.values().next().value;
    if (oldest === undefined) break;
    retiredExecutionIds.delete(oldest);
  }
}

/** Steered follow-ups share the running turn's execution, so a late receipt
 *  for an older request can name the current owner: that one keeps speaking. */
function retireStaleExecution(executionId: string): void {
  if (executionId !== currentOwner?.executionId) rememberRetired(executionId);
}

export function registerLiveTalkPromptRequest(
  clientRequestId: string,
  queuesBehindReply = false,
): void {
  const normalized = clientRequestId.trim();
  if (!normalized || registeredTurns.has(normalized)) return;
  registeredTurns.set(normalized, { order: ++nextOrder, queuesBehindReply });
  trimBoundedMap(registeredTurns);
}

export function adoptLiveTalkSpeechOwner(
  clientRequestId: string,
  executionId: string,
): LiveTalkOwnerDecision {
  const requestId = clientRequestId.trim();
  const nextExecutionId = executionId.trim();
  if (!requestId || !nextExecutionId) return "stale";
  if (
    currentOwner?.requestId === requestId &&
    currentOwner.executionId === nextExecutionId
  ) {
    return "duplicate";
  }
  const registered = registeredTurns.get(requestId);
  if (!registered) {
    retireStaleExecution(nextExecutionId);
    return "stale";
  }
  const turn = registered;
  if (turn.executionId && turn.executionId !== nextExecutionId) {
    retireStaleExecution(nextExecutionId);
    return "stale";
  }
  turn.executionId = nextExecutionId;
  if (turn.order <= latestOwnerOrder) {
    retireStaleExecution(nextExecutionId);
    return "stale";
  }
  // A turn queued behind the reply leaves that reply free to finish.
  if (currentOwner && !turn.queuesBehindReply) {
    rememberRetired(currentOwner.executionId);
  }
  retiredOwner = null;
  retiredExecutionIds.delete(nextExecutionId);
  currentOwner = {
    requestId,
    executionId: nextExecutionId,
    order: turn.order,
  };
  latestOwnerOrder = turn.order;
  trimBoundedMap(registeredTurns);
  return "adopted";
}

/** The user answered this execution's question, so what it says next answers
 *  them and may be spoken, unless a newer turn owns speech: the newest turn
 *  always wins, and queued requests keep their order. */
export function resumeAnsweredLiveTalkExecution(executionId: string): void {
  if (currentOwner && currentOwner.executionId !== executionId) return;
  retiredExecutionIds.delete(executionId);
  if (currentOwner) return;
  currentOwner = {
    requestId: `answer:${executionId}`,
    executionId,
    order: latestOwnerOrder,
  };
}

/** True when the turn sent as `clientRequestId` queues behind the reply. */
export function liveTalkTurnQueuesBehindReply(
  clientRequestId: string,
): boolean {
  return (
    registeredTurns.get(clientRequestId.trim())?.queuesBehindReply ?? false
  );
}

export function retireCurrentLiveTalkSpeechOwner(): string | null {
  const executionId = currentOwner?.executionId ?? null;
  if (executionId) rememberRetired(executionId);
  if (currentOwner) retiredOwner = currentOwner;
  currentOwner = null;
  return executionId;
}

/**
 * Gives speech back to the owner a user turn silenced, when that turn went
 * nowhere near it (it waits for the run to end) and no newer turn owns speech.
 * Returns the restored execution.
 */
export function restoreRetiredLiveTalkSpeechOwner(): string | null {
  if (currentOwner || !retiredOwner) return null;
  currentOwner = retiredOwner;
  retiredOwner = null;
  retiredExecutionIds.delete(currentOwner.executionId);
  return currentOwner.executionId;
}

export function currentLiveTalkSpeechOwnerExecutionId(): string | null {
  return currentOwner?.executionId ?? null;
}

export function isLiveTalkSpeechAllowed(executionId: string): boolean {
  return !retiredExecutionIds.has(executionId);
}

export function resetLiveTalkSpeechOwnershipForTests(): void {
  nextOrder = 0;
  latestOwnerOrder = 0;
  currentOwner = null;
  retiredOwner = null;
  registeredTurns.clear();
  retiredExecutionIds.clear();
}
